import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Avatar, CircularProgress
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'http://localhost:34512/api';

/**
 * Page de premier lancement (desktop uniquement).
 * Permet à l'utilisateur de définir son nom local.
 */
export default function DesktopSetup() {
  const navigate = useNavigate();
  const [name, setName]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    // Si déjà configuré → redirect dashboard
    const stored = localStorage.getItem('era_desktop_user');
    if (stored) navigate('/', { replace: true });
  }, [navigate]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Veuillez entrer votre nom.'); return; }
    setLoading(true);
    try {
      // Save name to backend SQLite (user id=1)
      await axios.put(`${API_BASE}/desktop/profile`, { full_name: name.trim() });

      // Store in localStorage so we skip this page next time
      localStorage.setItem('era_desktop_user', JSON.stringify({ full_name: name.trim() }));
      // Set a fake token so PrivateRoute passes
      localStorage.setItem('era_token', 'desktop-local-token');
      localStorage.setItem('era_user', JSON.stringify({ id: 1, name: name.trim(), role: 'admin' }));

      navigate('/', { replace: true });
    } catch (err) {
      setError('Erreur lors de l\'initialisation. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 5,
          borderRadius: 3,
          maxWidth: 420,
          width: '100%',
          textAlign: 'center'
        }}
      >
        {/* Logo */}
        <Typography variant="h3" fontWeight={900} sx={{ color: '#2196F3', mb: 0.5 }}>
          ERA<span style={{ color: '#1e293b' }}>PROJECT</span>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Application Desktop — Édition solo
        </Typography>

        <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', mx: 'auto', mb: 3 }}>
          <PersonIcon sx={{ fontSize: 40 }} />
        </Avatar>

        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Bienvenue !
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Comment voulez-vous être appelé dans ERA Project ?
        </Typography>

        <form onSubmit={handleStart}>
          <TextField
            fullWidth
            label="Votre nom"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            error={!!error}
            helperText={error}
            autoFocus
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Commencer à utiliser ERA Project'}
          </Button>
        </form>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          Toutes vos données sont stockées localement sur votre ordinateur.
          Aucune connexion Internet requise.
        </Typography>
      </Paper>
    </Box>
  );
}
